import { Box, Stack } from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { NumberFormatValues, NumericFormat } from "react-number-format";

import { isStringWithNumber } from "../../../../../utils";
import { PaymentBlockMessage } from "./PaymentBlockMessage";
import { InputWithCurrency } from "../../../../shared/Inputs";
import { useGetDebtStateQuery } from "../../../../../api/debt";

interface Props {
  amountFromInput: string | undefined;
  onChangeInput: (amount: string) => void;
}

const MAX_AVAILABLE_AMOUNT = 99999999; // 99млн

export const PaymentBlockInput: FC<Props> = ({ amountFromInput, onChangeInput }) => {
  const { data } = useGetDebtStateQuery();

  const [isTotalDebtAmountExceeded, setIsTotalDebtAmountExceeded] = useState<boolean>(false);

  const borderColor = isTotalDebtAmountExceeded ? "red.500" : "purple.500";
  const totalDebtAmount = isStringWithNumber(data?.total) ? Number(data?.total) : 0;

  // Disable error on select an option
  useEffect(() => {
    if (amountFromInput) {
      const clearedAmount = amountFromInput
        .replace(/[^0-9,.]/g, "")
        .replace(",", ".");

      const formattedAmount = isStringWithNumber(clearedAmount) ? Number(clearedAmount) : 0;

      if (formattedAmount <= totalDebtAmount) {
        setIsTotalDebtAmountExceeded(false);
      }
    }
  }, [amountFromInput, totalDebtAmount]);

  const onChange = (newAmount: string) => {
    if (newAmount && Number(newAmount) > totalDebtAmount) {
      setIsTotalDebtAmountExceeded(true);
    } else {
      setIsTotalDebtAmountExceeded(false);
    }

    onChangeInput(newAmount);
  };

  const validateValue = (values: NumberFormatValues) => {
    const { floatValue } = values;
    return floatValue === undefined || floatValue <= MAX_AVAILABLE_AMOUNT;
  };

  return (
    <Box w="100%">
      <Stack h="40px" px="15px" border="1px solid" borderRadius="12px" borderColor={borderColor}>
        <NumericFormat
          customInput={InputWithCurrency}
          value={amountFromInput ?? ""}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source as string === "event") {
              onChange(values.value);
            }
          }}
          isAllowed={validateValue}
          decimalSeparator=","
          decimalScale={2}
          fixedDecimalScale={false}
          allowNegative={false}
          allowLeadingZeros={false}
          thousandSeparator=" "
          // For InputWithCurrency
          color="brand.500"
          fontSize="20px"
          placeholder="Введите сумму платежа"
        />
      </Stack>

      <PaymentBlockMessage
        amountFromInput={amountFromInput}
        totalDebtAmount={totalDebtAmount}
        isTotalDebtAmountExceeded={isTotalDebtAmountExceeded}
      />
    </Box>
  );
};
